import { isDialablePhoneNumber } from './is-dialable-phone-number';

/*
 * Dial-destination contract with the FreeSWITCH `default` dialplan.
 *
 * The switch owns every number translation: its `default` context classifies a destination by
 * shape (`+E.164`, `0…` national, bare national, `00…` international, service short codes,
 * extensions, feature codes, named destinations) and formats it for the carrier exactly once, on
 * the gateway leg. The browser therefore sends what the agent dialed — minus cosmetic separators —
 * and never adds a prefix or a country code of its own.
 *
 * This file does not classify destinations and must not: the browser has no copy of the dialplan's
 * rules. Whether a call left through the carrier gateway is reported back by the dialplan itself
 * (the `X-CX-Outbound-Call` response header, read in `SipCoreService`).
 *
 * Canonical rules: `.claude/docs/FreeSwitch/dialplan-contexts-and-numbering.md` § 2 (the ordered
 * `default` table) and § 5.4 (what each producer sends).
 */

/**
 * Cosmetic separators {@link isDialablePhoneNumber} accepts (`"+90 532 123 45 67"`,
 * `"(212) 555-0123"`) but none of the dialplan's digit-only conditions match.
 */
const DIAL_SEPARATOR_PATTERN = /[\s\-().]/g;

/** Trims `value` and drops the cosmetic separators of {@link DIAL_SEPARATOR_PATTERN}. */
function stripDialSeparators(value: string): string {
  return value.trim().replace(DIAL_SEPARATOR_PATTERN, '');
}

/**
 * Prepares a dial destination for SIP.js. Number translation is the switch's job (see the contract
 * above), so this only tidies the agent's input.
 *
 * Applied centrally in {@link CallFacadeService.makeCall}, the single choke point every outbound dial
 * entry point (campaign "Call Now", manual dialpad number, dialer search row, queue-override dial)
 * already goes through — callers must not reshape the destination themselves.
 *
 * - A dialable phone number (see {@link isDialablePhoneNumber}) is trimmed and stripped of cosmetic
 *   separators (spaces, dashes, dots, parentheses), and nothing else: `+E.164` keeps its `+`, a `0…`
 *   national number keeps its trunk `0`, a bare national number gets no country code, an `00…`
 *   international number is passed through, short codes and extensions stay as they are.
 * - Anything else is returned unchanged — notably the queue dial string of a queue-override dial
 *   (`queue_<tenant>_<code>`, or the legacy `fifo_…` alias), which the dialplan matches verbatim.
 *
 * @param destination raw dial value about to be handed to SIP.js.
 * @returns the destination string that should actually be dialed.
 */
export function buildOutboundDialDestination(destination: string): string {
  if (!isDialablePhoneNumber(destination)) {
    return destination;
  }
  return stripDialSeparators(destination);
}
